import React, { useEffect, useRef, useState } from "react";
import { InfoTip } from "@/components/InfoTip";
import { toast } from "sonner";
import { ThumbsUp, Upload, Trash2, FileText, Smile, Frown, Eye, Download, FolderDown } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDeShort } from "@/lib/date";

export const openClientDoc = async (docId) => {
  const win = window.open("", "_blank");
  try {
    const r = await api.get(`/doc-check/document/${docId}/file`, { responseType: "blob" });
    win.location.href = URL.createObjectURL(r.data);
  } catch (e) { win?.close(); toast.error("Datei konnte nicht geöffnet werden"); }
};

const fetchPdf = async (url) => URL.createObjectURL((await api.get(url, { responseType: "blob", timeout: 90000 })).data);

function SystemDocRow({ v, testId }) {
  const [busy, setBusy] = useState("");
  const view = async () => {
    const win = window.open("", "_blank");
    setBusy("view");
    try { win.location.href = await fetchPdf(v.url); }
    catch (e) { win?.close(); toast.error("Dokument konnte nicht geöffnet werden"); } finally { setBusy(""); }
  };
  const download = async () => {
    setBusy("dl");
    try {
      const a = document.createElement("a");
      a.href = await fetchPdf(v.url); a.download = `${v.label.replace(/[^\wäöüÄÖÜß.-]+/g, "_")}.pdf`; a.click();
    } catch (e) { toast.error("Download fehlgeschlagen"); } finally { setBusy(""); }
  };
  return (
    <div className="flex items-center gap-2 mt-1 text-xs" data-testid={testId}>
      <FileText size={12} className="text-[#0a788c]" />
      <span className="text-slate-700 truncate">{v.label}</span>
      <span className="text-[10px] px-1.5 bg-teal-50 text-teal-700 border border-teal-100">System</span>
      <button onClick={view} disabled={!!busy} className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 hover:underline disabled:opacity-50" data-testid={`${testId}-view`}><Eye size={12} /> {busy === "view" ? "Lädt…" : "Ansehen"}</button>
      <button onClick={download} disabled={!!busy} className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 hover:underline disabled:opacity-50" data-testid={`${testId}-download`}><Download size={12} /> {busy === "dl" ? "Lädt…" : "Herunterladen"}</button>
    </div>
  );
}

function SlotRow({ slot, d, clientId, onChanged }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("slot", slot.key); fd.append("file", file);
    setBusy(true);
    try {
      await api.post(`/doc-check/client/${clientId}/upload`, fd, { timeout: 60000 });
      toast.success(`${slot.label} hochgeladen`);
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const remove = async (u) => {
    if (!window.confirm(`„${u.filename}" löschen?`)) return;
    await api.delete(`/doc-check/document/${u.doc_id}`);
    onChanged();
  };
  const state = d.present ? "present" : d.due ? "missing" : "notdue";
  return (
    <div className="px-5 py-3 border-t border-slate-100 flex items-start gap-4" data-testid={`doc-slot-${slot.key}`}>
      <div className={`w-8 h-8 shrink-0 flex items-center justify-center ${state === "present" ? "bg-emerald-50 text-emerald-600" : state === "missing" ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-400"}`}>
        {d.rating === "good" ? <Smile size={15} /> : d.rating === "bad" ? <Frown size={15} /> : state === "present" ? <ThumbsUp size={14} /> : <span className="text-[10px] font-bold">{state === "missing" ? "!" : "–"}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-slate-900 break-words">{slot.label}</div>
        <div className="text-[11px] text-slate-500" data-testid={`doc-slot-status-${slot.key}`}>
          {state === "present" ? (d.auto ? `im Portal vorhanden${d.info ? ` · ${d.info}` : ""}` : "hochgeladen") : state === "missing" ? "fehlt" : slot.optional ? "nur bei Bedarf" : "noch nicht fällig"}
        </div>
        {(d.views || []).map((v, i) => <SystemDocRow key={v.url + i} v={v} testId={`doc-system-${slot.key}-${i}`} />)}
        {d.uploads.map((u) => (
          <div key={u.doc_id} className="flex items-center gap-2 mt-1 text-xs" data-testid={`doc-upload-${u.doc_id}`}>
            <FileText size={12} className="text-slate-400" />
            <button onClick={() => openClientDoc(u.doc_id)} className="text-slate-700 hover:underline truncate">{u.filename}</button>
            <span className="text-slate-400 font-mono">{formatDeShort(u.uploaded_at)}</span>
            <button onClick={() => remove(u)} className="text-slate-400 hover:text-red-600" data-testid={`doc-upload-delete-${u.doc_id}`}><Trash2 size={12} /></button>
          </div>
        ))}
      </div>
      <input ref={ref} type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid={`doc-file-${slot.key}`} />
      <button onClick={() => ref.current?.click()} disabled={busy} className="px-3 py-1.5 text-xs border border-slate-300 hover:bg-slate-100 disabled:opacity-50 flex items-center gap-1.5 shrink-0" data-testid={`doc-upload-btn-${slot.key}`}>
        <Upload size={12} /> {busy ? "Lädt…" : (d.views || []).length ? "Zusätzlich hochladen" : "Hochladen"}
      </button>
    </div>
  );
}

export default function ClientDocumentsPanel({ clientId }) {
  const [data, setData] = useState(null);
  const [zipping, setZipping] = useState(false);
  const load = () => api.get(`/doc-check/client/${clientId}`).then((r) => setData(r.data))
    .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  useEffect(() => { load(); }, [clientId]); // eslint-disable-line
  const downloadZip = async () => {
    setZipping(true);
    try {
      const r = await api.get(`/doc-check/client/${clientId}/akte.zip`, { responseType: "blob", timeout: 300000 });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data); a.download = `Teilnehmerakte_${clientId}.zip`; a.click();
      toast.success("Akte heruntergeladen");
    } catch (e) { toast.error("Akte konnte nicht erzeugt werden"); } finally { setZipping(false); }
  };
  if (!data) return <div className="text-xs text-slate-400">Lädt…</div>;
  const missing = data.slots.filter((s) => data.docs[s.key].due && !data.docs[s.key].present);
  const pct = data.completeness;
  return (
    <div className="border border-slate-200 bg-white" data-testid="client-documents-panel">
      <div className="px-5 py-4 flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2">Unterlagen-Prüfung <InfoTip k="unterlagen" /></div>
          <div className="font-heading text-xl font-light tracking-tighter text-slate-900">Teilnehmer-Unterlagen</div>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={downloadZip} disabled={zipping} className="px-3 py-1.5 text-xs border border-slate-300 hover:bg-slate-100 disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="client-documents-zip">
            <FolderDown size={13} /> {zipping ? "Akte wird gepackt…" : "Komplette Akte (ZIP)"}
          </button>
          <div className="text-right">
            <div className="font-heading text-3xl font-light tracking-tighter text-slate-900" data-testid="client-documents-completeness">{pct.toLocaleString("de-DE")} %</div>
            <div className="text-[11px] text-slate-500">vollständig (fällige Unterlagen)</div>
          </div>
        </div>
      </div>
      <div className="px-5 pb-4" data-testid="client-documents-progress">
        <div className="h-2 bg-slate-100 overflow-hidden">
          <div className={`h-full transition-all duration-500 ${pct >= 100 ? "bg-emerald-500" : pct >= 60 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${Math.max(pct, 2)}%` }} />
        </div>
        {missing.length ? (
          <div className="mt-2 text-xs text-slate-600" data-testid="client-documents-missing">
            <span className="text-red-600 font-medium">{missing.length} Pflichtunterlage{missing.length === 1 ? "" : "n"} fehlt noch:</span>{" "}
            {missing.map((s) => s.label).join(" · ")}
          </div>
        ) : (
          <div className="mt-2 text-xs text-emerald-700" data-testid="client-documents-missing">Alle fälligen Pflichtunterlagen liegen vor.</div>
        )}
      </div>
      {data.slots.map((s) => <SlotRow key={s.key} slot={s} d={data.docs[s.key]} clientId={clientId} onChanged={load} />)}
    </div>
  );
}
